import React, { useCallback, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { StyledButton, StyledInput } from '../common/styledComponents'
import { Icon } from 'semantic-ui-react'
import { DatasetConfig } from '../../types'

interface StreamToConfluentCloudProps {
  datasetConfig: DatasetConfig
}

const StreamToConfluentCloud: React.FC<StreamToConfluentCloudProps> = ({
  datasetConfig,
}) => {
  const {
    watch,
    register,
    trigger
  } = useFormContext()

  const formValues = watch()

  const [showNotification, setShowNotification] = useState(false)

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>

    if (showNotification) {
      timer = setTimeout(() => {
        setShowNotification(false)
      }, 500)
    }

    return () => {
      if (timer) clearTimeout(timer)
    }
  }, [showNotification])

  const datasetConfigDownload = () => {
    const medusaRequestJsonString = JSON.stringify(datasetConfig.dataset)

    const datasetConfigFileContents = `${medusaRequestJsonString}`

    const blob = new Blob([datasetConfigFileContents], { type: 'text/plain' })
    const url = window.URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = url
    link.download = `dataset.json`
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }

  const createDockerCommand = () => {
    // todo - Adjust command for better formatting and spacing
    const baseCommand = [
      'docker run',
      `-v ./dataset.json:/project/dataset.json`,
      `-e PRODUCER_ENABLED=true`,
      `-e CREATE_TOPICS=true`,
      `-e BOOTSTRAP_SERVERS=${formValues.producerConfig.BOOTSTRAP_SERVERS?.trim()}`,
      `-e CONFLUENT_CLOUD_CLUSTER_KEY=${formValues.producerConfig.CLUSTER_API_KEY?.trim()}`,
      `-e CONFLUENT_CLOUD_CLUSTER_SECRET=${formValues.producerConfig.CLUSTER_API_SECRET?.trim()}`,
      `-e SCHEMA_REGISTRY_URL=${formValues.producerConfig.SCHEMA_REGISTRY_URL?.trim()}`,
      `-e SCHEMA_REGISTRY_API_ID=${formValues.producerConfig.SCHEMA_REGISTRY_API_ID?.trim()}`,
      `-e SCHEMA_REGISTRY_API_SECRET=${formValues.producerConfig.SCHEMA_REGISTRY_API_SECRET?.trim()}`
    ]

    if (formValues.producerConfig.ADDITIONAL_PRODUCER_PROPERTIES?.trim()) {
      baseCommand.push(
        `-e ADDITIONAL_PRODUCER_PROPERTIES=${formValues.producerConfig.ADDITIONAL_PRODUCER_PROPERTIES.trim()}`
      )
    }

    return `${baseCommand.join(' ')} ${window._env_.MEDUSA_IMAGE}`
  }

  const handleCopy = () => {
    trigger()
    navigator.clipboard.writeText(createDockerCommand().trim())
    setShowNotification(true)
  }

  interface InputProps {
    inputKey: string
    inputLabel: string
  }

  const Input = useCallback(
    (props: InputProps) => {
      const { inputKey, inputLabel } = props
      return (
        <div
          key={inputKey}
          style={{
            display: 'flex',
            width: '100%',
            alignItems: 'left',
            flexDirection: 'column',
            padding: '10px',
          }}
        >
          <div
            style={{
              whiteSpace: 'nowrap',
              color: '#fff',
              fontFamily: 'monospace',
              fontSize: '0.9em',
            }}
          >
            {inputLabel}
          </div>
          <StyledInput
            {...register(`producerConfig.${inputKey}`)}
            style={{
              width: '100%',
              padding: '10px',
              marginTop: '5px',
              color: '#111',
            }}
            autoComplete="off"
          />
        </div>
      )
    },
    [register]
  )

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        padding: '10px',
      }}
    >
      <div
        style={{
          color: '#fff',
          fontSize: '1em',
          fontFamily: 'monospace',
          marginBottom: '10px',
        }}
      >
        1.) Enter your Confluent Cloud Cluster details. (You can skip this step for now and enter
        them locally if you wish)
      </div>
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <Input
          inputKey="BOOTSTRAP_SERVERS"
          inputLabel="Bootstrap Servers"
        />
        <Input
          inputKey="SCHEMA_REGISTRY_URL"
          inputLabel="Schema Registry URL"
        />
      </div>
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <Input
          inputKey="CLUSTER_API_KEY"
          inputLabel="Cluster API Key"
        />
        <Input
          inputKey="SCHEMA_REGISTRY_API_ID"
          inputLabel="Schema Registry API ID"
        />
      </div>
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <Input
          inputKey="CLUSTER_API_SECRET"
          inputLabel="Cluster API Secret"
        />
        <Input
          inputKey="SCHEMA_REGISTRY_API_SECRET"
          inputLabel="Schema Registry API Secret"
        />
      </div>
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <Input
          inputKey="ADDITIONAL_PRODUCER_PROPERTIES"
          inputLabel="Additional Producer Properties (Optional)"
        />
      </div>

      <div
        style={{
          color: '#fff',
          fontSize: '1em',
          fontFamily: 'monospace',
          marginTop: '20px',
          marginBottom: '10px',
        }}
      >
        2.) Download your 'dataset.json' file.
      </div>
      <div>
        <StyledButton onClick={datasetConfigDownload}>
          <Icon name="download" />
          Download Dataset
        </StyledButton>
      </div>

      <div
        style={{
          color: '#fff',
          fontSize: '1em',
          fontFamily: 'monospace',
          marginTop: '20px',
          marginBottom: '10px',
        }}
      >
        3.) Copy the following command and run it locally.
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ marginRight: '20px' }}>
          <StyledButton
            style={{ width: '150px' }}
            onClick={handleCopy}
          >
            {!showNotification && (
              <>
                <Icon name="copy" />
                Copy Command
              </>
            )}
            {showNotification && <>Copied</>}
          </StyledButton>
        </div>
        <pre
          style={{
            fontFamily: 'courier',
            padding: '10px',
            borderRadius: '8px',
            fontSize: '0.9em',
            marginBottom: '10px',
            backgroundColor: '#333',
            color: '#fff',
            overflowX: 'auto',
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
            border: '1px solid #222',
          }}
        >
          {createDockerCommand().trim()}
        </pre>
      </div>
    </div>
  )
}

export default StreamToConfluentCloud
